import React from 'react';
import { useTranslation } from 'react-i18next';

export type PaymentMethodType =
  | 'electronic'
  | 'network_transfer'
  | 'manual_adjustment'
  | 'cash_store'
  | 'unknown';

export const PAYMENT_METHOD_TYPES: { value: PaymentMethodType; label: string }[] = [
  { value: 'electronic',        label: 'Electronic (PSP)' },
  { value: 'network_transfer',  label: 'Network Transfer' },
  { value: 'manual_adjustment', label: 'Manual Adjustment' },
  { value: 'cash_store',        label: 'Cash Store' },
  { value: 'unknown',           label: 'Unknown' },
];

interface PaymentMethodTypeFilterProps {
  selected: PaymentMethodType | undefined;
  onChange: (value: PaymentMethodType | undefined) => void;
}

// Tailwind badge classes per payment_method_type. Used both in the filter
// chip and in the FinancialReport table column so colors stay consistent.
export function paymentMethodTypeBadgeClasses(t: string | undefined): string {
  switch (t) {
    case 'electronic':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
    case 'network_transfer':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    case 'manual_adjustment':
    case 'cash_store':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
    default:
      return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  }
}

export function paymentMethodTypeLabel(t: string | undefined): string {
  const found = PAYMENT_METHOD_TYPES.find((p) => p.value === t);
  return found ? found.label : (t || '');
}

export const PaymentMethodTypeFilter: React.FC<PaymentMethodTypeFilterProps> = ({ selected, onChange }) => {
  const { t } = useTranslation();
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('filters.paymentType', { defaultValue: 'Payment Type' })}
      </label>
      <select
        value={selected ?? ''}
        onChange={(e) => onChange(e.target.value ? (e.target.value as PaymentMethodType) : undefined)}
        className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
      >
        <option value="">{t('filters.allPaymentTypes', { defaultValue: 'All types' })}</option>
        {PAYMENT_METHOD_TYPES.map((p) => (
          <option key={p.value} value={p.value}>{p.label}</option>
        ))}
      </select>
    </div>
  );
};
